import { MOBILE, ROTATION } from '../game/constants';
import type { InputRouter } from './InputRouter';

/**
 * The subset of `DeviceOrientationEvent` the gyro path reads.
 *
 * The DOM type does not declare `requestPermission` (an iOS 13+ extension), so
 * both it and the event shape are described here and reached for through a
 * narrow cast rather than assumed to exist — which is also what keeps this from
 * throwing on desktop Chromium, where neither is present.
 */
interface OrientationReading {
  /** Left/right tilt, -90..90 in portrait. */
  gamma: number | null;
  beta: number | null;
  alpha: number | null;
}

interface OrientationConstructor {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default' | string>;
}

/** Wrap an angle difference in degrees into (-180, 180]. */
function wrap180(deg: number): number {
  return ((((deg + 180) % 360) + 360) % 360) - 180;
}

/**
 * Device gyroscope → board angular velocity.
 *
 * The board is turned by the phone's LEFT/RIGHT TILT measured against a NEUTRAL
 * captured when the player enables the gyro (and re-captured by `calibrate()`),
 * so the control is relative to how they are holding the device, not to the
 * world. Within the dead-zone nothing happens; tilt up to `gyroFullTiltDeg`
 * beyond the neutral maps linearly to full board speed.
 *
 * It reports through the shared `InputRouter` under the 'gyro' source, so it
 * yields to a live drag and wins over the on-screen buttons — one board, one
 * resolved target. It is active whenever input is (including the Countdown, which
 * is the aiming window), because the router is applied every physics step.
 */
export class GyroController {
  private supported =
    typeof window !== 'undefined' && typeof window.DeviceOrientationEvent !== 'undefined';
  private enabled = false;
  private neutralGamma: number | null = null;
  private lastGamma: number | null = null;
  private handleOrientation: ((event: Event) => void) | null = null;
  private handleVisibility: (() => void) | null = null;

  constructor(private readonly router: InputRouter) {}

  get isSupported(): boolean {
    return this.supported;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  get neutralDeg(): number | null {
    return this.neutralGamma;
  }

  /**
   * Start steering from tilt. Returns whether gyro control is now active.
   *
   * On iOS 13+ the user gesture that triggered this call is REQUIRED before the
   * sensor is exposed, so `requestPermission` is awaited (only if present) and a
   * non-'granted' result returns false — never throws, so it cannot trip the
   * "no console/page errors" verification gate.
   */
  async enable(): Promise<boolean> {
    if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
      this.supported = false;
      return false;
    }
    this.supported = true;

    const ctor = window.DeviceOrientationEvent as unknown as OrientationConstructor;
    if (typeof ctor.requestPermission === 'function') {
      try {
        const result = await ctor.requestPermission();
        if (result !== 'granted') return false;
      } catch {
        return false;
      }
    }

    this.teardownListeners();

    this.handleOrientation = (event: Event) => this.onOrientation(event as unknown as OrientationReading);
    window.addEventListener('deviceorientation', this.handleOrientation);

    // Sending the browser to the background briefly hides it; stop the board
    // drifting behind the UI rather than steering from a stale reading.
    this.handleVisibility = () => {
      if (document.hidden) this.router.set('gyro', 0, false);
    };
    document.addEventListener('visibilitychange', this.handleVisibility);

    this.enabled = true;
    return true;
  }

  /** Re-capture neutral from the latest reading — the "校平" action. */
  calibrate(): void {
    if (this.lastGamma != null) this.neutralGamma = this.lastGamma;
  }

  disable(): void {
    this.teardownListeners();
    this.enabled = false;
    this.router.set('gyro', 0, false);
  }

  dispose(): void {
    this.disable();
  }

  private onOrientation(event: OrientationReading): void {
    if (event.gamma == null) return;
    this.lastGamma = event.gamma;

    // The first reading after enabling IS the neutral — where they were holding.
    if (this.neutralGamma == null) {
      this.neutralGamma = event.gamma;
      this.router.set('gyro', 0, false);
      return;
    }

    const delta = wrap180(event.gamma - this.neutralGamma);
    const magnitude = Math.abs(delta) - MOBILE.gyroDeadZoneDeg;
    if (magnitude <= 0) {
      this.router.set('gyro', 0, false);
      return;
    }

    const span = Math.max(1e-3, MOBILE.gyroFullTiltDeg - MOBILE.gyroDeadZoneDeg);
    const fraction = Math.min(1, magnitude / span);
    const direction = delta > 0 ? 1 : -1;
    const sign = direction * (MOBILE.invertSign ? -1 : 1);
    this.router.set('gyro', sign * fraction * ROTATION.maxOmega, true);
  }

  private teardownListeners(): void {
    if (this.handleOrientation) {
      window.removeEventListener('deviceorientation', this.handleOrientation);
      this.handleOrientation = null;
    }
    if (this.handleVisibility) {
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.handleVisibility = null;
    }
  }
}
